import React, { useState, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormGroup,
  FormControlLabel,
  Checkbox,
  TextField,
  Alert,
  AlertTitle,
  Chip,
  Divider,
  LinearProgress,
  IconButton,
  Link,
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  Image as ImageIcon,
  Close as CloseIcon,
  CheckCircle as DoneIcon,
  ErrorOutline as FailIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { SocialPlatform } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { getPlatform, getPlatformName, getPlatformColor, isPlatformAvailable } from '../config/platforms';
import { themeNames, themeSlots, nextOccurrence, formatTime, ThemeSlot } from '../config/postingSchedule';
import { thumbnailUrl } from '../utils/media';

const API_URL = process.env.REACT_APP_API_URL || '';

const STEPS = ['Type', 'Platforms', 'When', 'Content', 'Review'];

interface PlannedPost {
  platform: SocialPlatform;
  when: Date;
  slot: ThemeSlot;
}

interface Result {
  platform: string;
  ok: boolean;
  detail: string;
}

/**
 * Guided post creation: pick a type, and the day, time and platforms come from the posting
 * schedule rather than from memory.
 *
 * The point is to remove the thinking, not to add a layer. A theme like "Employee spotlight"
 * is already declared in postingSchedule.ts as LinkedIn Monday 9am, Facebook Monday 11am and
 * Instagram Monday noon — this reads that in reverse.
 *
 * One post per platform, each at its own slot time, rather than one post targeting three
 * platforms at a single time. Two reasons: each platform then publishes in its own best
 * window, and each can fail, be edited or be rescheduled independently — which is how the
 * outcome model already works everywhere else.
 */
export const GuidedPost: React.FC = () => {
  const navigate = useNavigate();
  const { token, user } = useAuth();

  const [step, setStep] = useState(0);
  const [theme, setTheme] = useState('');
  const [chosen, setChosen] = useState<SocialPlatform[]>([]);
  // Per-platform times, keyed by platform, so one can be nudged without losing the others
  const [times, setTimes] = useState<Record<string, Date>>({});
  const [content, setContent] = useState('');
  const [media, setMedia] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [results, setResults] = useState<Result[] | null>(null);
  const [error, setError] = useState('');
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const slotsByTheme = useMemo(() => themeSlots(), []);
  const themes = useMemo(() => themeNames(), []);

  // Only themes with at least one slot on a platform this build can publish to, and that a
  // restricted account is allowed to use
  const allowed = (p: SocialPlatform) =>
    isPlatformAvailable(p) && (!user?.allowedPlatforms || user.allowedPlatforms.includes(p));

  const themeOptions = useMemo(
    () => themes.filter(t => (slotsByTheme.get(t) || []).some(s => allowed(s.platform))),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [themes, slotsByTheme, user?.allowedPlatforms]
  );

  const slots = (slotsByTheme.get(theme) || []).filter(s => allowed(s.platform));

  /** Choosing a theme presets every platform and time it implies. */
  const pickTheme = (value: string) => {
    setTheme(value);
    const themeSlotList = (slotsByTheme.get(value) || []).filter(s => allowed(s.platform));
    setChosen(themeSlotList.map(s => s.platform));
    setTimes(
      Object.fromEntries(themeSlotList.map(s => [s.platform, nextOccurrence(s.day, s.time)]))
    );
  };

  const planned: PlannedPost[] = chosen
    .map(p => {
      const slot = slots.find(s => s.platform === p);
      return slot && times[p] ? { platform: p, when: times[p], slot } : null;
    })
    .filter((x): x is PlannedPost => Boolean(x));

  // Blocking problems, checked before the Review step so nothing is refused after submitting
  const problems = planned.flatMap(({ platform, when }) => {
    const config = getPlatform(platform);
    const out: string[] = [];
    if (config?.charLimit && content.length > config.charLimit) {
      out.push(`${getPlatformName(platform)}: ${content.length} characters exceeds its ${config.charLimit.toLocaleString()} limit`);
    }
    if (config?.requiresMedia && media.length === 0) {
      out.push(`${getPlatformName(platform)} requires an image or video`);
    }
    if (when.getTime() <= Date.now()) {
      out.push(`${getPlatformName(platform)}: that time has already passed`);
    }
    return out;
  });

  const handleUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setError('');
    setUploading(true);
    try {
      const r = await fetch(`${API_URL}/api/media`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': file.type },
        body: file,
      });
      const data = await r.json();
      if (r.ok) setMedia(prev => [...prev, data.url]);
      else setError(data.error || 'Upload failed');
    } catch {
      setError('Network error uploading file');
    } finally {
      setUploading(false);
    }
  };

  /**
   * Create and schedule one post per platform.
   *
   * Sequential rather than parallel, and each outcome recorded separately: if LinkedIn is
   * accepted and Instagram is refused, the person needs to know which — a single "failed"
   * would leave them unsure what exists.
   */
  const scheduleAll = async () => {
    setSubmitting(true);
    setError('');
    const out: Result[] = [];

    for (const { platform, when } of planned) {
      try {
        const r = await fetch(`${API_URL}/api/posts`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            content,
            platforms: [platform],
            mediaUrls: media,
            scheduledFor: when.toISOString(),
            status: 'scheduled',
          }),
        });
        const data = await r.json();
        out.push(
          r.ok
            ? { platform, ok: true, detail: `Scheduled for ${when.toLocaleString(undefined, { weekday: 'short', hour: 'numeric', minute: '2-digit' })}` }
            : { platform, ok: false, detail: data.message || data.error || 'Rejected' }
        );
      } catch {
        out.push({ platform, ok: false, detail: 'Network error' });
      }
    }

    setResults(out);
    setSubmitting(false);
  };

  // ---------------------------------------------------------------- done screen
  if (results) {
    const ok = results.filter(r => r.ok);
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            {ok.length === results.length
              ? `Scheduled ${ok.length} post${ok.length === 1 ? '' : 's'}`
              : `Scheduled ${ok.length} of ${results.length}`}
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 3 }}>
            {results.map(r => (
              <Box key={r.platform} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <Box sx={{ color: r.ok ? 'success.main' : 'error.main', display: 'flex', mt: 0.25 }}>
                  {r.ok ? <DoneIcon fontSize="small" /> : <FailIcon fontSize="small" />}
                </Box>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {getPlatformName(r.platform)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">{r.detail}</Typography>
                </Box>
              </Box>
            ))}
          </Box>

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button variant="contained" onClick={() => navigate('/calendar')}>
              Open Calendar
            </Button>
            <Button
              onClick={() => {
                // Keep nothing: the next post is a different post
                setResults(null);
                setStep(0);
                setTheme('');
                setChosen([]);
                setTimes({});
                setContent('');
                setMedia([]);
              }}
            >
              Set up another
            </Button>
          </Box>
        </CardContent>
      </Card>
    );
  }

  // ---------------------------------------------------------------- steps
  const canContinue =
    (step === 0 && Boolean(theme)) ||
    (step === 1 && chosen.length > 0) ||
    step === 2 ||
    (step === 3 && content.trim().length > 0 && !uploading) ||
    step === 4;

  return (
    <Card>
      <CardContent>
        <Stepper activeStep={step} sx={{ mb: 3 }}>
          {STEPS.map(label => (
            <Step key={label}><StepLabel>{label}</StepLabel></Step>
          ))}
        </Stepper>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

        {/* ---- 1. Type ---- */}
        {step === 0 && (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Pick the kind of post and the schedule fills in the rest — which networks, which
              day, what time.
            </Typography>
            <FormControl fullWidth>
              <InputLabel>Post type</InputLabel>
              <Select value={theme} label="Post type" onChange={e => pickTheme(e.target.value)}>
                {themeOptions.map(t => {
                  const s = (slotsByTheme.get(t) || []).filter(x => allowed(x.platform));
                  return (
                    <MenuItem key={t} value={t}>
                      {t}
                      <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                        — {s.map(x => getPlatformName(x.platform)).join(', ')}
                        {s.every(x => x.occasional) && ' · as needed'}
                      </Typography>
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
            {theme && (
              <Alert severity="info" sx={{ mt: 2 }}>
                {slots.map(s => `${getPlatformName(s.platform)} ${s.day} ${formatTime(s.time)}`).join(' · ')}
              </Alert>
            )}
          </>
        )}

        {/* ---- 2. Platforms ---- */}
        {step === 1 && (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              All the networks this post type normally goes to. Untick any you want to skip.
            </Typography>
            <FormGroup>
              {slots.map(s => (
                <FormControlLabel
                  key={s.platform}
                  control={
                    <Checkbox
                      checked={chosen.includes(s.platform)}
                      onChange={e =>
                        setChosen(prev =>
                          e.target.checked ? [...prev, s.platform] : prev.filter(p => p !== s.platform)
                        )
                      }
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: getPlatformColor(s.platform) }} />
                      {getPlatformName(s.platform)}
                      <Typography component="span" variant="caption" color="text.secondary">
                        {s.day} {formatTime(s.time)}
                      </Typography>
                    </Box>
                  }
                />
              ))}
            </FormGroup>
          </>
        )}

        {/* ---- 3. When ---- */}
        {step === 2 && (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Taken from the schedule — the next time each slot comes round. Adjust any of them
              if this post needs a different time.
            </Typography>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {planned.map(({ platform, when }) => (
                  <DateTimePicker
                    key={platform}
                    label={getPlatformName(platform)}
                    value={when}
                    onChange={v => v && setTimes(prev => ({ ...prev, [platform]: v }))}
                    disablePast
                    slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                  />
                ))}
              </Box>
            </LocalizationProvider>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
              Each platform publishes at its own time, so every one lands in its best window.
              See the{' '}
              <Link component="button" onClick={() => navigate('/schedule')} sx={{ verticalAlign: 'baseline' }}>
                posting schedule
              </Link>
              .
            </Typography>
          </>
        )}

        {/* ---- 4. Content ---- */}
        {step === 3 && (
          <>
            <TextField
              label="Post content"
              value={content}
              onChange={e => setContent(e.target.value)}
              multiline
              minRows={6}
              fullWidth
              placeholder={`What is the ${theme.toLowerCase()} about?`}
            />
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1, mb: 2 }}>
              {planned.map(({ platform }) => {
                const limit = getPlatform(platform)?.charLimit;
                const over = Boolean(limit && content.length > limit);
                return (
                  <Chip
                    key={platform}
                    size="small"
                    label={`${getPlatformName(platform)} ${content.length}/${limit?.toLocaleString()}`}
                    color={over ? 'error' : 'default'}
                    variant="outlined"
                  />
                );
              })}
            </Box>

            <Divider sx={{ my: 2 }} />

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>Media</Typography>
              <Button size="small" startIcon={<ImageIcon />} disabled={uploading} onClick={() => fileInputRef.current?.click()}>
                Add
              </Button>
              <input ref={fileInputRef} type="file" accept="image/*,video/*" hidden onChange={handleUpload} />
            </Box>
            {uploading && <LinearProgress sx={{ mb: 1 }} />}
            {media.length === 0 ? (
              <Typography variant="caption" color="text.secondary">
                {planned.some(p => getPlatform(p.platform)?.requiresMedia)
                  ? 'Required — Instagram rejects text-only posts.'
                  : 'Optional.'}
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {media.map(url => (
                  <Box key={url} sx={{ position: 'relative' }}>
                    <Box component="img" src={thumbnailUrl(url, 320)} alt="" loading="lazy" decoding="async" sx={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 1 }} />
                    <IconButton
                      size="small"
                      onClick={() => setMedia(prev => prev.filter(m => m !== url))}
                      sx={{ position: 'absolute', top: -8, right: -8, bgcolor: 'background.paper', boxShadow: 1, '&:hover': { bgcolor: 'background.paper' } }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            )}
          </>
        )}

        {/* ---- 5. Review ---- */}
        {step === 4 && (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {planned.length} separate post{planned.length === 1 ? '' : 's'} will be scheduled —
              one per network, each at its own time.
            </Typography>

            {problems.length > 0 && (
              <Alert severity="error" sx={{ mb: 2 }}>
                <AlertTitle>Fix these first</AlertTitle>
                <Box component="ul" sx={{ pl: 2.5, m: 0 }}>
                  {problems.map(p => <li key={p}><Typography variant="body2">{p}</Typography></li>)}
                </Box>
              </Alert>
            )}

            <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, overflow: 'hidden', mb: 2 }}>
              {planned.map(({ platform, when }, i) => (
                <Box key={platform} sx={{ p: 1.5, borderTop: i ? 1 : 0, borderColor: 'divider', display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
                  <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: getPlatformColor(platform), flexShrink: 0 }} />
                  <Typography variant="body2" sx={{ fontWeight: 500, minWidth: 110 }}>
                    {getPlatformName(platform)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {when.toLocaleString(undefined, { weekday: 'long', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                  </Typography>
                </Box>
              ))}
            </Box>

            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              {theme} · {content.length.toLocaleString()} characters
              {media.length > 0 && ` · ${media.length} media`}
            </Typography>
            <Box sx={{ p: 1.5, bgcolor: 'action.hover', borderRadius: 1, maxHeight: 140, overflow: 'auto' }}>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{content}</Typography>
            </Box>
          </>
        )}

        <Box sx={{ display: 'flex', gap: 1, mt: 3 }}>
          <Button disabled={step === 0 || submitting} onClick={() => setStep(s => s - 1)}>
            Back
          </Button>
          <Box sx={{ flexGrow: 1 }} />
          {step < STEPS.length - 1 ? (
            <Button variant="contained" disabled={!canContinue} onClick={() => setStep(s => s + 1)}>
              Continue
            </Button>
          ) : (
            <Button
              variant="contained"
              disabled={submitting || problems.length > 0 || planned.length === 0}
              onClick={scheduleAll}
            >
              {submitting ? 'Scheduling…' : `Schedule ${planned.length} post${planned.length === 1 ? '' : 's'}`}
            </Button>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};
